import { useMemo } from 'react';

import { Box, Button, Heading, Icon, Modal, Paragraph, Text } from '@zillow/constellation-10';

import { getFreemiumProductByType } from '../../../utils/upsellUtils';

import { PREMIUM_FEATURES_BY_CONTEXT, PRODUCT_TYPE_NAME_TO_ID } from '../../../constants/FreemiumConstants';
import ModularListingCSEvents from '../../../model/ClickstreamEvent/ModularListingCSEvents';

import MultiplexWarningBanner from '../../../../../src/components/MultiplexWarningBanner';

import type { FreemiumProduct } from '../../Freemium/interfaces';

const PremiumPurchaseModal = ({
  onClose,
  onSelectUpgrade,
  freemiumProducts,
  isInBuildingGroup = false,
}: {
  onClose: () => void;
  onSelectUpgrade: () => void;
  freemiumProducts: Array<FreemiumProduct>;
  isInBuildingGroup?: boolean;
}) => {
  const upgradedProduct = useMemo(
    () => getFreemiumProductByType(freemiumProducts, PRODUCT_TYPE_NAME_TO_ID.PREMIUM),
    [freemiumProducts],
  );

  const renderPremiumUpsellItemList = () => {
    return Object.entries(PREMIUM_FEATURES_BY_CONTEXT).map(([key, value]) => {
      const { HEADING, BODY, ICON: IconComponent } = value.PAY_AND_PUBLISH;

      return (
        <Box key={key} css={{ display: 'flex', flexDirection: 'row', marginBottom: '600' }}>
          {/* need div container to prevent mismatching icon sizes */}
          <div>
            <Icon size="lg" css={{ marginRight: '400', color: 'icon.accent.blue.hero' }} render={<IconComponent />} />
          </div>

          <Box>
            <Paragraph textStyle="body-lg-bold">{HEADING}</Paragraph>
            <Paragraph textStyle="body">{BODY}</Paragraph>
          </Box>
        </Box>
      );
    });
  };

  return (
    <Modal
      data-testid="PremiumPurchaseModal"
      size={{
        base: 'fullScreen',
        lg: 'md',
      }}
      open={true}
      onOpenChange={(open) => !open && onClose()}
      header={
        <Heading level={1} textStyle="heading-xs">
          About premium
        </Heading>
      }
      body={
        <div>
          <MultiplexWarningBanner isInBuildingGroup={isInBuildingGroup} />
          <Heading css={{ marginBottom: '800' }} level={4}>
            Get the edge you need to fill your listing fast
          </Heading>
          <div>{renderPremiumUpsellItemList()}</div>
          <Box
            css={{
              paddingTop: '800',
              paddingBottom: '600',
              display: 'flex',
              justifyContent: 'space-between',
              borderTop: '1px solid {colors.border.subtle}',
            }}
          >
            <Text textStyle="heading-xs">Upgrade your listing to premium</Text>
            <Text textStyle="body-lg-bold">{upgradedProduct?.price}</Text>
          </Box>
        </div>
      }
      footer={
        <Button
          size="md"
          tone="brand"
          emphasis="filled"
          onClick={onSelectUpgrade}
          data-eventid={ModularListingCSEvents.ClickUpgradeToPremiumFromModal.data_event_id}
          data-testid="PremiumPurchaseModal-UpgradeToPremiumButton"
        >
          Upgrade to premium
        </Button>
      }
    />
  );
};

export default PremiumPurchaseModal;
